import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { portalApi } from '../../api/portal';
import { useCompany } from '../../contexts/CompanyContext';
import { Search, KeyRound, Building2 } from 'lucide-react';
import toast from 'react-hot-toast';
import type { PortalCompanySearchResult } from '../../types';
import PageHeader from '../../components/PageHeader';

export default function PortalJoinCompany() {
  const navigate = useNavigate();
  const { refreshCompanies } = useCompany();
  const [tab, setTab] = useState<'search' | 'code'>('code');
  const [code, setCode] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<PortalCompanySearchResult[]>([]);
  const [searching, setSearching] = useState(false);

  const joinMutation = useMutation({
    mutationFn: (joinCode: string) => portalApi.joinCompany(joinCode),
    onSuccess: async (result) => {
      if (result.alreadyMember) {
        toast('You are already a member of this company');
      } else {
        toast.success(`Joined ${result.companyName}!`);
      }
      await refreshCompanies();
      navigate('/portal');
    },
    onError: (err: any) => toast.error(err.message || 'Failed to join company'),
  });

  const handleSearch = async () => {
    if (searchQuery.trim().length < 2) return;
    setSearching(true);
    try {
      const results = await portalApi.searchCompanies(searchQuery.trim());
      setSearchResults(results);
      if (results.length === 0) toast('No companies found matching your search');
    } catch (err: any) {
      toast.error(err.message || 'Search failed');
    } finally {
      setSearching(false);
    }
  };

  const handleCodeSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!code.trim()) return;
    joinMutation.mutate(code.trim());
  };

  return (
    <div className="flex flex-col gap-4 pb-10">
      <PageHeader
        title="Join a Company"
        intro="Enter a join code provided by the company, or search for companies accepting new members."
      />

      {/* Tab buttons */}
      <div className="flex gap-2">
        <button
          onClick={() => setTab('code')}
          className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
            tab === 'code'
              ? 'bg-primary text-white'
              : 'bg-row-odd text-muted hover:text-fg'
          }`}
        >
          <KeyRound className="w-4 h-4" />
          Enter Code
        </button>
        <button
          onClick={() => setTab('search')}
          className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
            tab === 'search'
              ? 'bg-primary text-white'
              : 'bg-row-odd text-muted hover:text-fg'
          }`}
        >
          <Search className="w-4 h-4" />
          Search Companies
        </button>
      </div>

      {/* Code tab */}
      {tab === 'code' && (
        <form onSubmit={handleCodeSubmit} className="card space-y-4">
          <div>
            <label className="label">Join Code</label>
            <input
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder="e.g., ABC12345"
              className="input text-center text-lg tracking-wider font-mono"
              maxLength={12}
            />
          </div>
          <button
            type="submit"
            disabled={!code.trim() || joinMutation.isPending}
            className="btn btn-primary w-full disabled:opacity-50"
          >
            {joinMutation.isPending ? 'Joining...' : 'Join Company'}
          </button>
        </form>
      )}

      {/* Search tab */}
      {tab === 'search' && (
        <div className="space-y-4">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                placeholder="Type company name..."
                className="input pl-9"
              />
            </div>
            <button onClick={handleSearch} disabled={searching} className="btn btn-primary">
              {searching ? 'Searching...' : 'Search'}
            </button>
          </div>

          {searchResults.length > 0 && (
            <div className="card" style={{ padding: 0 }}>
              {searchResults.map((company) => (
                <div
                  key={company.id}
                  className="flex items-center justify-between px-6 py-4 border-b border-line-light last:border-0"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-primary-muted flex items-center justify-center">
                      <Building2 className="w-4 h-4 text-primary" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-fg">{company.name}</p>
                      {company.industry && <p className="text-xs text-muted">{company.industry}</p>}
                    </div>
                  </div>
                  <p className="text-xs text-muted">
                    A join code is required. Contact this company for a code.
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
